import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, LinearProgress, ToggleButton, ToggleButtonGroup, Tooltip, Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { documentsApi, isCancelled } from './api';
import { EXPORT_FORMATS, EXPORT_MODES, SORT_LABELS } from './constants';
import { apiBlobError, apiError, downloadBlob, filterLabels, plural } from './format';
import { DocumentFilters, DocumentsMeta, ExportCount, ExportFormat, ExportMode, SortState } from './types';
import { notify } from '../../notifications';

interface Props {
  open: boolean;
  meta: DocumentsMeta;
  /** Применённые фильтры списка - выгрузка идёт ровно по ним */
  filters: DocumentFilters;
  /** Текущая сортировка таблицы - в том же порядке строки попадут в файл */
  sort: SortState;
  onClose: () => void;
}

const BORDER = '#e2e8f0';
const PRICES_UNAVAILABLE = 'prices-unavailable';

const sectionLabel = { mb: 0.75, fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary' } as const;
const filterChipSx = { height: 24, fontSize: '0.74rem', borderRadius: 1.5, bgcolor: 'rgba(37,99,235,0.07)', color: '#1e40af', fontWeight: 600 } as const;
const toggleSx = { '& .MuiToggleButton-root': { px: 1.25, py: 0, fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', whiteSpace: 'nowrap' } } as const;

const defaultColumns = (count: ExportCount): string[] => count.columns.filter(c => c.default).map(c => c.key);

/** Выгрузка списка документов: строка на документ или на позицию, колонки по выбору, до 100 000 строк в файле */
const DocumentsExportDialog: React.FC<Props> = ({ open, meta, filters, sort, onClose }) => {
  const [mode, setMode] = useState<ExportMode>('documents');
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  // выбор колонок - отдельно для каждого режима; пока режим не загружен, берутся колонки по умолчанию
  const [columnsByMode, setColumnsByMode] = useState<Partial<Record<ExportMode, string[]>>>({});
  const [showColumns, setShowColumns] = useState(false);
  const [count, setCount] = useState<ExportCount | null>(null);
  const [counting, setCounting] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const downloadCtrl = useRef<AbortController | null>(null);

  const labels = useMemo(() => filterLabels(filters, meta).map(l => l.label), [filters, meta]);
  const sortLabel = `${SORT_LABELS[sort.sortBy] || sort.sortBy}, ${sort.sortOrder === 'asc' ? 'по возрастанию' : 'по убыванию'}`;
  const modeInfo = EXPORT_MODES.find(m => m.value === mode) || EXPORT_MODES[0];
  const productFilter = Boolean(filters.product.trim() || filters.products.length);

  useEffect(() => {
    if (!open) return;
    setShowColumns(false);
    setCount(null);
    setCountError(null);
  }, [open]);

  // Незавершённая выгрузка отменяется при размонтировании
  useEffect(() => () => downloadCtrl.current?.abort(), []);

  useEffect(() => {
    if (!open) return undefined;
    const ctrl = new AbortController();
    // колонки прежнего режима к новому не подходят
    setCount(prev => (prev && prev.mode === mode ? prev : null));
    setCounting(true);
    documentsApi.exportCount(mode, filters, ctrl.signal)
      .then(res => {
        setCount(res);
        setCountError(null);
        setCounting(false);
        setColumnsByMode(prev => (prev[mode] ? prev : { ...prev, [mode]: defaultColumns(res) }));
      })
      .catch(err => {
        if (isCancelled(err)) return;
        setCountError(apiError(err, 'Не удалось подсчитать строки выгрузки'));
        setCounting(false);
      });
    return () => ctrl.abort();
  }, [open, mode, filters]);

  const catalog = useMemo(() => (count && count.mode === mode ? count.columns : []), [count, mode]);
  // в файл уходят только колонки, доступные по правам сейчас, и в порядке справочника
  const columns = useMemo(() => {
    const picked = columnsByMode[mode] || [];
    return catalog.filter(c => picked.indexOf(c.key) !== -1).map(c => c.key);
  }, [catalog, columnsByMode, mode]);

  const toggleColumn = useCallback((key: string) => {
    setColumnsByMode(prev => {
      const picked = prev[mode] || [];
      return { ...prev, [mode]: picked.indexOf(key) === -1 ? [...picked, key] : picked.filter(k => k !== key) };
    });
  }, [mode]);

  const reset = useCallback(() => {
    setFormat('xlsx');
    if (count && count.mode === mode) setColumnsByMode(prev => ({ ...prev, [mode]: defaultColumns(count) }));
  }, [count, mode]);

  const close = useCallback(() => {
    downloadCtrl.current?.abort();
    onClose();
  }, [onClose]);

  const download = useCallback(async () => {
    const ctrl = new AbortController();
    downloadCtrl.current = ctrl;
    setDownloading(true);
    try {
      const file = await documentsApi.exportFile(mode, filters, sort, format, columns, ctrl.signal);
      downloadBlob(file.filename, file.blob);
      if (file.warning === PRICES_UNAVAILABLE) {
        notify.warning('Сервис цен был недоступен: цены и суммы в файле заполнены не полностью, подробности - в примечании файла');
      }
      onClose();
    } catch (err) {
      if (!isCancelled(err)) notify.error(await apiBlobError(err, 'Не удалось сформировать выгрузку'));
    } finally {
      downloadCtrl.current = null;
      setDownloading(false);
    }
  }, [mode, filters, sort, format, columns, onClose]);

  const ready = count !== null && count.mode === mode;
  const tooLarge = ready && Boolean(count?.tooLarge);
  const empty = ready && count?.count === 0;
  const files = ready && count ? count.files : 0;
  const canDownload = !downloading && !counting && ready && !tooLarge && !empty && columns.length > 0;
  const noun = mode === 'items' ? 'Позиций' : 'Документов';

  return (
    <Dialog
      open={open} onClose={downloading ? undefined : close} maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 660, borderRadius: 3, boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ py: 1.5, px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${BORDER}` }}>
        <Typography variant="subtitle1" fontWeight={700}>Выгрузка документов</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ToggleButtonGroup
            size="small" exclusive value={format} disabled={downloading}
            onChange={(_, v: ExportFormat | null) => { if (v) setFormat(v); }}
            sx={{ height: 30, ...toggleSx }}
          >
            {EXPORT_FORMATS.map(f => <ToggleButton key={f.value} value={f.value}>{f.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <IconButton onClick={close} size="small" aria-label="Закрыть" sx={{ color: 'text.secondary', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ mt: 2.5 }}>
          <Typography variant="body2" sx={sectionLabel}>Что выгружать</Typography>
          <ToggleButtonGroup
            size="small" exclusive value={mode} disabled={downloading}
            onChange={(_, v: ExportMode | null) => { if (v) { setMode(v); setShowColumns(false); } }}
            sx={{ height: 32, ...toggleSx }}
          >
            {EXPORT_MODES.map(m => <ToggleButton key={m.value} value={m.value} sx={{ px: '14px !important' }}>{m.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{modeInfo.hint}</Typography>
        </Box>

        <Box>
          <Typography variant="body2" sx={sectionLabel}>Выгружаются документы по текущим фильтрам списка</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {labels.length
              ? labels.map(l => <Chip key={l} size="small" label={l} sx={{ ...filterChipSx, maxWidth: '100%' }} />)
              : <Chip size="small" label="Все документы в вашей зоне видимости" sx={filterChipSx} />}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Порядок строк: {sortLabel}. Чтобы изменить набор документов, закройте окно и поменяйте фильтры или сортировку таблицы.
          </Typography>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500 }}>Колонки в файле:</Typography>
              <Typography variant="caption" color="text.secondary">
                {ready ? `Выбрано ${columns.length} из ${catalog.length}` : '…'}
              </Typography>
            </Box>
            <Button
              size="small" variant="text" onClick={() => setShowColumns(v => !v)} disabled={!ready}
              sx={{ textTransform: 'none', fontSize: '0.75rem', height: 26, borderRadius: 1.25, color: showColumns ? 'primary.main' : 'text.secondary' }}
            >
              {showColumns ? 'Свернуть' : 'Настроить…'}
            </Button>
          </Box>

          <Collapse in={showColumns && ready} timeout={180} unmountOnExit>
            <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, border: `1px solid ${BORDER}`, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {catalog.map(col => {
                const checked = columns.indexOf(col.key) !== -1;
                return (
                  <Chip
                    key={col.key} label={col.title} size="small" clickable disabled={downloading}
                    onClick={() => toggleColumn(col.key)}
                    icon={checked ? <CheckIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                    color={checked ? 'primary' : 'default'} variant={checked ? 'filled' : 'outlined'}
                    sx={{ borderRadius: 1.5, height: 28, fontSize: '0.75rem', fontWeight: checked ? 600 : 400 }}
                  />
                );
              })}
            </Box>
          </Collapse>
          {ready && columns.length === 0 && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>Выберите хотя бы одну колонку</Typography>
          )}
        </Box>

        {mode === 'items' && productFilter && (
          <Alert severity="info">
            Задан фильтр «Товар»: в файл попадут только позиции выбранного товара, остальные позиции этих документов не выгружаются.
          </Alert>
        )}
        {tooLarge && count && (
          <Alert severity="warning">
            Строк больше допустимого предела выгрузки ({count.maxRows.toLocaleString('ru-RU')}). Сузьте период или добавьте фильтры.
          </Alert>
        )}
        {!tooLarge && count && files > 1 && (
          <Alert severity="info">
            Строк больше {count.partRows.toLocaleString('ru-RU')}: выгрузка будет разделена на {files} {plural(files, 'файл', 'файла', 'файлов')} и упакована в ZIP-архив.
          </Alert>
        )}
        {empty && (
          <Alert severity="info">
            {mode === 'items' ? 'В документах по текущим фильтрам нет позиций - выгружать нечего.' : 'По текущим фильтрам документов нет - выгружать нечего.'}
          </Alert>
        )}
        {countError && <Alert severity="error">{countError}</Alert>}
        {downloading && (
          <Box>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Формируем файл. Большие выгрузки занимают несколько минут - окно закрывать не нужно.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ py: 1.5, px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.25, borderRadius: 1.5, bgcolor: 'rgba(25, 118, 210, 0.06)', height: 32 }}>
          <Typography variant="caption" color="text.secondary">{noun}:</Typography>
          {counting
            ? <CircularProgress size={12} thickness={5} />
            : <Typography variant="caption" fontWeight={700} color="primary.main">{ready && count ? count.count.toLocaleString('ru-RU') : '-'}</Typography>}
          {files > 1 && !counting && (
            <Tooltip title="Количество файлов в архиве">
              <Typography variant="caption" color="text.secondary">Файлов: {files}</Typography>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button size="small" color="inherit" onClick={reset} disabled={downloading} sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary', minWidth: 0, px: 1 }}>
            Сбросить
          </Button>
          <Button size="small" color="inherit" onClick={close} sx={{ textTransform: 'none', fontSize: '0.8rem', minWidth: 0, px: 1.25 }}>
            Отмена
          </Button>
          <Button
            variant="contained" size="small" onClick={download} disabled={!canDownload}
            startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <FileDownloadIcon fontSize="small" />}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', height: 34, px: 2, borderRadius: 1.5, whiteSpace: 'nowrap', boxShadow: 'none' }}
          >
            {downloading ? 'Формирование…' : files > 1 ? 'Скачать ZIP' : `Скачать ${format.toUpperCase()}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default memo(DocumentsExportDialog);
